import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, Cpu, Eye, Mountain, CloudSnow, Mic, Hand, Radio, Package } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Navigation from "@/components/Navigation";
import d5Hero from "@/assets/robots/pudu-d5-hero.png";
import d5Features from "@/assets/robots/pudu-d5-features.png";

const PuduD5 = () => {
  const benefits = [
    { Icon: Cpu, title: "NVIDIA-Powered Compute", desc: "NVIDIA Orin + RK3588 dual-processor architecture delivering up to 275 TOPS." },
    { Icon: Eye, title: "360° Perception", desc: "Dual 96-line LiDAR (192-line upgrade available) and four fisheye cameras for centimeter-level positioning." },
    { Icon: Mountain, title: "All-Terrain Mobility", desc: "Handles 25 cm steps, climbs 30°, descends 45°, cruises up to 5 m/s across complex environments." },
    { Icon: CloudSnow, title: "All-Weather Adaptability", desc: "IP67 ingress protection, cold-start at −10°C, operates from −20°C to 55°C." },
    { Icon: Mic, title: "Voice & Sound", desc: "6-mic array with AI noise reduction and sound localization for accurate response." },
    { Icon: Hand, title: "Gesture Interaction", desc: "Recognizes 10+ natural gestures including stand, follow, and crawl for fluid collaboration." },
    { Icon: Radio, title: "Remote Vision & Control", desc: "Real-time remote operation with live panoramic video feed from the field." },
    { Icon: Package, title: "Modular Expansion", desc: "Open architecture supports delivery crate, inspection kit, and charging station add-ons." },
  ];

  const specs = [
    { label: "Compute Platform", value: "NVIDIA Jetson Orin AGX 64GB + RK3588" },
    { label: "AI Performance", value: "Up to 275 TOPS" },
    { label: "Thermal Design", value: "Vapor-chamber system + dual-fan cooling" },
    { label: "Perception (Standard)", value: "Dual 96-line LiDAR + 4 fisheye cameras" },
    { label: "Perception (Upgrade)", value: "Dual 192-line LiDAR available" },
    { label: "Positioning", value: "Centimeter-level, dynamic obstacle avoidance" },
    { label: "Max Step Height", value: "25 cm (D5) / 30 cm (D5-W)" },
    { label: "Slope Capability", value: "Up to 30° climb, 45° descent" },
    { label: "Max Speed", value: "5 m/s" },
    { label: "Standing Dimensions", value: "900 × 543 × 572 mm" },
    { label: "Weight", value: "61 kg (134.5 lbs) / D5-W 62 kg" },
    { label: "Maximum Joint Torque", value: "165 N·m (121.7 lb·ft)" },
    { label: "Battery Capacity", value: "1000 Wh" },
    { label: "Operating Time", value: "2–2.5 h (D5) / 2–3 h (D5-W)" },
    { label: "Walking Payload", value: "20–30 kg (44–66 lbs)" },
    { label: "Cold-Start", value: "Below −10°C (Below 14°F)" },
    { label: "Operating Temperature", value: "−20°C to 55°C (−4°F to 131°F)" },
    { label: "Ingress Protection", value: "IP67" },
    { label: "Interaction", value: "Voice, gesture, autonomous following" },
    { label: "Audio", value: "6-mic array, 10W speaker, AI noise reduction" },
  ];

  const accessories = [
    { title: "Metal Delivery Crate", desc: "Secure load-bearing module for autonomous delivery in logistics and campus environments." },
    { title: "Inspection Kit", desc: "Add-on perception payload for industrial inspection, monitoring, and asset checks." },
    { title: "Charging Station", desc: "Autonomous charging support for sustained, unattended field operations." },
  ];

  const industries = [
    "Industrial inspection", "Security patrol", "Campus patrol", "Warehouse inspection",
    "Logistics support", "Research environments", "Public service operations", "Complex terrain environments",
  ];

  return (
    <>
      <SEO
        title="PUDU D5 Quadruped Robot — Inspection, Security & Delivery | Neil AI"
        description="PUDU D5 industry-grade autonomous quadruped robot. NVIDIA-powered 275 TOPS, dual LiDAR 360° perception, IP67 all-weather, all-terrain mobility for inspection, security, and logistics."
        keywords="PUDU D5, quadruped robot, autonomous inspection robot, security patrol robot, NVIDIA Orin robot, all-terrain robot dog, industrial inspection robot"
      />
      <Navigation />
      <div className="min-h-screen pt-32 md:pt-40 pb-12 px-4" style={{
        background: `radial-gradient(900px 600px at 10% 10%, #132552 0%, #0B1736 45%, #060f24 100%)`
      }}>
        <main className="max-w-7xl mx-auto">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/robots">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back to Robots
            </Link>
          </Button>

          {/* Hero */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16 items-center">
            <div>
              <Badge className="mb-3 bg-primary/15 text-primary border border-primary/30">PUDU X-Lab · Advanced Robotics</Badge>
              <p className="text-primary font-semibold mb-2">Industry-Grade Autonomous Quadruped</p>
              <h1 className="font-orbitron font-bold text-3xl md:text-5xl lg:text-6xl mb-6">
                PUDU <span className="text-primary">D5</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground mb-6 leading-relaxed">
                Industry-grade autonomous quadruped robot built for inspection, delivery, logistics, security, research, and complex environments where traditional wheeled robots may not be enough.
              </p>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <Card className="glass-effect border-[hsl(var(--line))] p-4 text-center">
                  <p className="text-2xl md:text-3xl font-bold text-primary mb-1">275</p>
                  <p className="text-xs text-muted-foreground">TOPS Compute</p>
                </Card>
                <Card className="glass-effect border-[hsl(var(--line))] p-4 text-center">
                  <p className="text-2xl md:text-3xl font-bold text-primary mb-1">IP67</p>
                  <p className="text-xs text-muted-foreground">All-Weather</p>
                </Card>
                <Card className="glass-effect border-[hsl(var(--line))] p-4 text-center">
                  <p className="text-2xl md:text-3xl font-bold text-primary mb-1">5 m/s</p>
                  <p className="text-xs text-muted-foreground">Max Cruise</p>
                </Card>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" variant="secondary" className="glow-gold">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Request D5 Demo
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="#use-cases">Explore Inspection Use Cases</a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="tel:3369624539">Talk To Robotics Specialist</a>
                </Button>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[hsl(var(--line))] bg-white/5">
              <img src={d5Hero} alt="PUDU D5 industry-grade autonomous quadruped robot" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Benefits */}
          <section className="mb-16">
            <h2 className="font-orbitron font-bold text-3xl mb-2 text-center">Built for <span className="text-primary">Complex Environments</span></h2>
            <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">Core capabilities engineered for inspection, security, logistics, and field operations.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {benefits.map(({ Icon, title, desc }) => (
                <Card key={title} className="glass-effect border-[hsl(var(--line))] p-6 hover:border-primary/40 transition-colors">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-orbitron font-semibold text-lg mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground">{desc}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* 360 perception + all-terrain feature block */}
          <section className="mb-16 grid lg:grid-cols-2 gap-8 items-center">
            <div className="rounded-2xl overflow-hidden border border-[hsl(var(--line))] bg-white/5">
              <img src={d5Features} alt="PUDU D5 perception and mobility features" className="w-full h-full object-cover" />
            </div>
            <div>
              <h2 className="font-orbitron font-bold text-3xl mb-4">360° Perception. All-Terrain Mobility.</h2>
              <p className="text-muted-foreground mb-6">
                Dual high-precision LiDAR and four fisheye cameras deliver full-surround sensing with centimeter-level accuracy. The NVIDIA Orin + RK3588 platform offers 275 TOPS of computing power with efficient thermal design for stable high-load operation across rain, snow, dust, and fluctuating climate conditions.
              </p>
              <ul className="space-y-3">
                {[
                  "Autonomous navigation with full-surround sensing",
                  "Dynamic obstacle avoidance in real time",
                  "Intelligent follow mode and natural gesture control",
                  "Cold-start at −10°C and IP67 ingress protection",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Specs */}
          <section className="mb-16">
            <h2 className="font-orbitron font-bold text-3xl mb-8">Technical Specifications</h2>
            <Card className="glass-effect border-[hsl(var(--line))] p-6">
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
                {specs.map((s) => (
                  <div key={s.label} className="border-b border-[hsl(var(--line))] pb-3">
                    <p className="text-sm text-muted-foreground mb-1">{s.label}</p>
                    <p className="font-semibold">{s.value}</p>
                  </div>
                ))}
              </div>
            </Card>
          </section>

          {/* Accessories */}
          <section className="mb-16">
            <h2 className="font-orbitron font-bold text-3xl mb-8">Optional Modules & Accessories</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {accessories.map((a) => (
                <Card key={a.title} className="glass-effect border-[hsl(var(--line))] p-6">
                  <h3 className="font-orbitron font-semibold text-lg mb-2 text-primary">{a.title}</h3>
                  <p className="text-sm text-muted-foreground">{a.desc}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* Use cases */}
          <section id="use-cases" className="mb-16">
            <h2 className="font-orbitron font-bold text-3xl mb-8">Industry Use Cases</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {industries.map((i) => (
                <Card key={i} className="glass-effect border-[hsl(var(--line))] p-5 text-center">
                  <p className="font-semibold text-sm">{i}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* Staff support / operational visibility */}
          <section className="mb-16">
            <Card className="glass-effect border-[hsl(var(--line))] p-8 md:p-10">
              <h2 className="font-orbitron font-bold text-2xl md:text-3xl mb-4">Designed to Support Your Teams</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                The PUDU D5 helps organizations extend automation into spaces that require mobility, inspection, remote visibility, or flexible delivery beyond standard wheeled robots. It supports security teams, facility teams, logistics teams, and inspection crews by reducing repetitive walking routes, manual monitoring, and difficult terrain checks.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                The result: improved operational awareness, fewer repetitive patrols, and real-time remote visibility into complex environments.
              </p>
            </Card>
          </section>

          {/* CTA */}
          <section className="mb-10">
            <Card className="glass-effect border-[hsl(var(--line))] p-8 md:p-10 text-center">
              <h2 className="font-orbitron font-bold text-2xl md:text-3xl mb-4">Ready to deploy the D5?</h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Book a live demo, request a custom configuration, or explore financing through our partner Ascentium.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg" variant="secondary" className="glow-gold">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Request D5 Demo
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                    Apply for Financing
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="tel:3369624539">Talk To Robotics Specialist</a>
                </Button>
              </div>
            </Card>
          </section>

          <p className="text-xs text-muted-foreground text-center">
            Specifications based on official PUDU D5 product information. Actual performance may vary by environment and configuration.
          </p>
        </main>
      </div>
    </>
  );
};

export default PuduD5;
